import React from 'react';
import { TrendingUp, TrendingDown, Package, DollarSign } from 'lucide-react';

function StockMovementAnalysis({ data }) {
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-montserrat">No hay datos de movimiento de stock</p>
      </div>
    );
  }

  const { fast_moving = [], slow_moving = [], inventory_value = 0 } = data;

  return (
    <div className="space-y-6">
      {/* Inventory Value */}
      <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 rounded-xl p-6 border-l-4 border-[#B1336E]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center shadow-md">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-slate-600 font-montserrat">Valor Total de Inventario</p>
            <p className="text-3xl font-bold text-[#B1336E] font-montserrat">
              S/ {inventory_value.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fast Moving Products */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#67A43C]/10 to-[#67A43C]/5 border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#67A43C]" />
              Productos de Rápida Rotación
            </h3>
            <p className="text-xs text-slate-600 font-montserrat mt-1">
              Productos con mayor volumen de ventas
            </p>
          </div>
          
          {fast_moving.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-slate-500 font-montserrat">No hay datos disponibles</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 font-montserrat">
                      Producto
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Entregado
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Pedidos
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fast_moving.map((product, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 font-montserrat">
                            {product.title}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat">{product.sku}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center px-2 py-1 bg-[#67A43C]/10 text-[#67A43C] rounded-full text-xs font-bold font-montserrat">
                          {product.total_delivered}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-slate-600 font-montserrat">
                          {product.transaction_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Slow Moving Products */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-amber-600" />
              Productos de Lenta Rotación
            </h3>
            <p className="text-xs text-slate-600 font-montserrat mt-1">
              Productos con bajo movimiento pero con stock
            </p>
          </div>
          
          {slow_moving.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-slate-500 font-montserrat">No hay datos disponibles</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 font-montserrat">
                      Producto
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Stock
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Vendido
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {slow_moving.map((product, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 font-montserrat">
                            {product.title}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat">{product.sku}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-bold font-montserrat">
                          {product.current_stock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-slate-600 font-montserrat">
                          {product.total_delivered}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StockMovementAnalysis;